import React, { useEffect, useState, useCallback } from 'react';
import { DatasetStatistic, Dataset } from '../../../types/dataset';
import { AlgorithmVersion } from '../../../types';
import { AlgorithmStatistic } from '../../../types/result';
import { Doughnut } from 'react-chartjs-2';

type Props = {
  statistics: DatasetStatistic[];
  selectedDataset: null | Dataset;
  selectedDatasetStatistics: null | DatasetStatistic;
  compareVersionId: number | null;
  algorithmVersions: AlgorithmVersion[];
};

export const ChartScorePerfect = ({
  statistics,
  selectedDataset,
  selectedDatasetStatistics,
  compareVersionId,
  algorithmVersions,
}: Props) => {
  const [chartValues, setChartValues] = useState<number[]>([]);

  const determineStatsForAverageView = useCallback(
    (compareVersionId: number | null) => {
      const latest = algorithmVersions[0];
      const statsForLabels = statistics.map(stat => {
        const filteredStats = stat.stats.filter((algorithmStat: AlgorithmStatistic) =>
          compareVersionId
            ? algorithmStat.algorithmVersion.id === latest.id ||
              parseInt(algorithmStat.algorithmVersion.id) === +compareVersionId
            : algorithmStat.algorithmVersion.id === latest.id
        );
        return filteredStats;
      });
      const stats: AlgorithmStatistic[] = [];
      statsForLabels.forEach(el => stats.push(...el));
      return stats;
    },
    [algorithmVersions, statistics]
  );

  //CHART chartValues
  useEffect(() => {
    if (!selectedDataset) {
      const latest = algorithmVersions[0];
      const stats = determineStatsForAverageView(compareVersionId);
      const previousValues = compareVersionId
        ? stats.filter(stat => parseInt(stat.algorithmVersion.id) === +compareVersionId)
        : null;
      const latestValues = stats.filter(stat => stat.algorithmVersion.id === latest.id);
      const avgPerfectScore =
        (latestValues.reduce((total, next) => total + next.summary.perfect, 0) * 100) / latestValues.length;
      setChartValues([avgPerfectScore]);
      if (previousValues && previousValues.length && compareVersionId) {
        const avgPerfectScore2 =
          (previousValues.reduce((total, next) => total + next.summary.perfect, 0) * 100) / previousValues.length;
        setChartValues(prev => [...prev, avgPerfectScore2]);
      }
    }
    if (selectedDataset && selectedDatasetStatistics?.stats.length) {
      setChartValues([selectedDatasetStatistics.stats[0].summary.perfect * 100]);
      if (compareVersionId && selectedDatasetStatistics.stats.length > 1) {
        const compareVersionStats = selectedDatasetStatistics.stats.find(
          stat => parseInt(stat.algorithmVersion.id) === +compareVersionId
        );
        if (compareVersionStats) {
          setChartValues(prev => [...prev, compareVersionStats.summary.perfect * 100]);
        }
      }
    }
  }, [selectedDataset, selectedDatasetStatistics, compareVersionId, algorithmVersions, determineStatsForAverageView]);

  //CHART DATASETS
  const data = {
    labels: ['Fully Correct', 'Not fully correct'],
    datasets: chartValues.map((value, index) => {
      return {
        label: index === 0 ? 'latest' : 'previous',
        data: [value, 100 - value],
        backgroundColor: [`rgba(47, 181, 33, ${0.6 - index * 0.2})`, `rgba(181, 33, 33, ${0.6 - index * 0.2})`],
      };
    }),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Percentage of Perfect Fields',
        font: { size: '16px' },
      },
    },
  };

  return (
    <>
      <Doughnut data={data} options={options} redraw={false} style={{ height: 350 }} />
    </>
  );
};
